<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3';
import {
    ArrowRight,
    Download,
    MousePointerClick,
    FileSearch,
    Table,
    FileBarChart,
    Sparkles,
    Keyboard,
    ShieldCheck,
    Zap,
    Layers,
    Eye,
    Columns3,
    BrainCircuit,
    BarChart3,
    FileSpreadsheet,
    BookOpen,
    Settings,
    GitBranch,
} from 'lucide-vue-next';
import FooterSection from '@/components/landing/FooterSection.vue';
import { Button } from '@/components/ui/button';
import AppHeaderLayoutLanding from '@/layouts/app/AppHeaderLayoutLanding.vue';
import { register } from '@/routes';

withDefaults(
    defineProps<{
        canRegister: boolean;
    }>(),
    {
        canRegister: true,
    },
);

const steps = [
    {
        number: '01',
        label: 'Import',
        title: 'Importez vos références en quelques clics',
        icon: Download,
        description:
            "Commencez par rassembler toutes vos sources. Connectez votre bibliothèque Zotero en un clic (OAuth), ou importez vos fichiers RIS, BibTeX ou CSV. L'outil détecte les doublons automatiquement et vous propose de les fusionner.",
        extended:
            "Dès le projet créé, décrivez votre sujet de recherche en texte libre. L'IA analyse votre description pour pré-remplir automatiquement vos critères d'inclusion/exclusion, les langues ciblées, les bases de données et votre stratégie de recherche. Vous gardez le contrôle et pouvez tout ajuster.",
        highlights: [
            {
                icon: BookOpen,
                text: 'Connexion Zotero avec OAuth — vos collections et groupes accessibles',
            },
            {
                icon: FileSpreadsheet,
                text: 'Import RIS, BibTeX et CSV — compatible avec tous les gestionnaires',
            },
            {
                icon: GitBranch,
                text: 'Déduplication automatique basée sur DOI, titre et auteurs',
            },
            {
                icon: Sparkles,
                text: 'Pré-remplissage IA de vos critères PICO depuis la description du projet',
            },
            {
                icon: Settings,
                text: "Génération d'équations de recherche pour PubMed, Embase, Cochrane...",
            },
        ],
    },
    {
        number: '02',
        label: 'Sélection des articles',
        title: "Triez des milliers d'articles avec méthode",
        icon: MousePointerClick,
        description:
            "La sélection des articles est l'étape la plus chronophage d'une revue systématique. Pour chaque article, vous décidez s'il est à retenir, à écarter ou à réexaminer — manuellement ou avec l'aide de l'IA.",
        extended:
            "En mode manuel, naviguez rapidement entre les articles grâce aux raccourcis clavier. En mode assisté (Pro), l'algorithme analyse le titre et le résumé de chaque référence en les confrontant à vos critères d'éligibilité. Il retourne un statut suggéré, un score de confiance et une justification détaillée. Chaque suggestion est une recommandation — jamais une décision finale. Vous validez ou corrigez en un clic.",
        highlights: [
            {
                icon: Keyboard,
                text: 'Raccourcis clavier pour retenir, écarter ou mettre de côté',
            },
            {
                icon: BrainCircuit,
                text: "L'IA analyse titre + résumé vs vos critères d'éligibilité",
            },
            {
                icon: BarChart3,
                text: 'Score de confiance pour chaque suggestion',
            },
            {
                icon: ShieldCheck,
                text: 'Chaque décision est traçable : qui a décidé, quand, pourquoi',
            },
            {
                icon: Sparkles,
                text: 'Justification en langage naturel pour chaque article',
            },
        ],
    },
    {
        number: '03',
        label: 'Rattachement des textes intégraux',
        title: 'Liez vos documents à vos références automatiquement',
        icon: FileSearch,
        description:
            "Avant d'extraire les données, chaque article inclus doit être associé à son PDF. L'outil propose un pipeline automatique en deux étapes pour gagner du temps.",
        extended:
            "Étape 1 : matching exact par DOI — si le PDF contient un DOI identique à une référence, l'association est immédiate et fiable. Étape 2 : pour les PDFs sans DOI correspondant, l'IA compare les métadonnées du fichier (titre extrait, auteurs, année) aux références candidates et propose le meilleur match avec un score de confiance. Vous pouvez aussi glisser-déposer manuellement vos fichiers sur les bonnes références.",
        highlights: [
            {
                icon: Zap,
                text: 'Matching instantané par DOI — fiabilité maximale',
            },
            {
                icon: BrainCircuit,
                text: 'Fallback IA par métadonnées quand le DOI est absent',
            },
            {
                icon: BarChart3,
                text: 'Score de confiance affiché pour chaque association proposée',
            },
            {
                icon: Layers,
                text: 'Glisser-déposer pour les associations manuelles',
            },
            { icon: Eye, text: 'Aperçu visuel du PDF avant validation' },
        ],
    },
    {
        number: '04',
        label: 'Extraction des données',
        title: 'Structurez les données de vos articles',
        icon: Table,
        description:
            "Définissez votre grille d'extraction avec des colonnes personnalisées (texte, nombre, date, booléen, sélection). L'IA peut pré-remplir la grille en parcourant le texte intégral de chaque PDF.",
        extended:
            "Commencez par définir vos colonnes — ou laissez l'IA les suggérer en analysant votre sujet et vos critères d'éligibilité. Pour chaque article retenu avec un texte intégral associé, l'IA extrait les données pertinentes cellule par cellule, avec un score de confiance. Vous vérifiez, corrigez et validez. Le mode manuel reste toujours disponible pour saisir ou compléter les données directement.",
        highlights: [
            {
                icon: Columns3,
                text: 'Colonnes typées : texte, nombre, date, booléen, sélection',
            },
            {
                icon: Sparkles,
                text: "Suggestion automatique de colonnes par l'IA",
            },
            {
                icon: BrainCircuit,
                text: 'Extraction IA depuis le texte intégral des PDFs',
            },
            {
                icon: BarChart3,
                text: 'Score de confiance par cellule pour guider la vérification',
            },
            {
                icon: ShieldCheck,
                text: 'Validation manuelle obligatoire avant export',
            },
        ],
    },
    {
        number: '05',
        label: 'PRISMA & Export',
        title: 'Générez votre diagramme et exportez tout',
        icon: FileBarChart,
        description:
            "Votre diagramme PRISMA 2020 se construit automatiquement au fil de vos décisions. Chaque étape du processus — identification, sélection sur titre et résumé, évaluation de l'éligibilité, inclusion — est comptabilisée avec les motifs d'écartement détaillés.",
        extended:
            "Le diagramme intègre vos métriques automatiques (articles importés, sélectionnés, retenus) et vos métriques manuelles (sources externes, textes intégraux indisponibles). Vous pouvez personnaliser les motifs d'écartement à chaque étape. Exportez vos données en CSV ou Excel pour vos analyses, et le diagramme PRISMA pour votre publication.",
        highlights: [
            {
                icon: BarChart3,
                text: 'Diagramme PRISMA 2020 conforme, généré automatiquement',
            },
            {
                icon: Settings,
                text: "Motifs d'écartement personnalisables à chaque étape",
            },
            {
                icon: FileSpreadsheet,
                text: "Export CSV et Excel de la grille d'extraction",
            },
            {
                icon: ShieldCheck,
                text: 'Vos données vous appartiennent — exportable à tout moment',
            },
            {
                icon: Sparkles,
                text: 'Métriques calculées automatiquement depuis vos décisions',
            },
        ],
    },
];
</script>

<template>
    <Head title="Workflow" />

    <AppHeaderLayoutLanding :canRegister="canRegister">
        <!-- Hero -->
        <section class="pt-12 pb-20">
            <div class="mx-auto max-w-5xl px-4 text-center">
                <h1
                    class="text-4xl font-semibold tracking-tight text-foreground sm:text-5xl"
                >
                    Un workflow en
                    <span class="text-brand">5 étapes</span>
                </h1>
                <p class="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground">
                    De l'import de vos références à la génération du diagramme
                    PRISMA. Chaque étape est conçue pour la rigueur
                    méthodologique et la productivité.
                </p>
            </div>
        </section>

        <!-- Steps navigation -->
        <section
            class="sticky top-20 z-10 border-y border-border/40 bg-background/95 backdrop-blur-sm"
        >
            <div class="mx-auto max-w-5xl px-4">
                <div
                    class="flex items-center justify-between overflow-x-auto py-3"
                >
                    <a
                        v-for="step in steps"
                        :key="step.number"
                        :href="`#step-${step.number}`"
                        class="flex items-center gap-2.5 px-3 py-1.5 text-sm font-medium whitespace-nowrap text-muted-foreground transition-colors hover:text-foreground"
                    >
                        <span class="text-xs tracking-widest text-brand">{{
                            step.number
                        }}</span>
                        {{ step.label }}
                    </a>
                </div>
            </div>
        </section>

        <!-- Step deep-dives -->
        <div class="py-16">
            <div class="mx-auto max-w-5xl space-y-32 px-4">
                <section
                    v-for="(step, index) in steps"
                    :key="step.number"
                    :id="`step-${step.number}`"
                    class="scroll-mt-36"
                >
                    <div
                        class="grid items-start gap-12 lg:grid-cols-2 lg:gap-20"
                        :class="index % 2 !== 0 ? '' : ''"
                    >
                        <!-- Text side -->
                        <div :class="index % 2 !== 0 ? 'lg:order-2' : ''">
                            <div class="mb-5 flex items-center gap-3">
                                <span
                                    class="text-3xl font-light tracking-tight text-brand/30"
                                >
                                    {{ step.number }}
                                </span>
                                <div
                                    class="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand/5 px-3 py-1 text-xs font-medium text-brand"
                                >
                                    <component
                                        :is="step.icon"
                                        class="h-3.5 w-3.5"
                                    />
                                    {{ step.label }}
                                </div>
                            </div>

                            <h2
                                class="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl"
                            >
                                {{ step.title }}
                            </h2>

                            <p
                                class="mt-5 text-base leading-relaxed text-muted-foreground"
                            >
                                {{ step.description }}
                            </p>

                            <p
                                class="mt-4 text-base leading-relaxed text-muted-foreground"
                            >
                                {{ step.extended }}
                            </p>
                        </div>

                        <!-- Highlights side -->
                        <div :class="index % 2 !== 0 ? 'lg:order-1' : ''">
                            <div class="space-y-4">
                                <div
                                    v-for="highlight in step.highlights"
                                    :key="highlight.text"
                                    class="flex items-start gap-4 rounded-xl border border-border/40 p-5 transition-colors hover:border-border hover:bg-accent/20"
                                >
                                    <div
                                        class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-brand/10"
                                    >
                                        <component
                                            :is="highlight.icon"
                                            class="h-4.5 w-4.5 text-brand"
                                        />
                                    </div>
                                    <span
                                        class="mt-1 text-sm leading-relaxed text-muted-foreground"
                                    >
                                        {{ highlight.text }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Separator -->
                    <div
                        v-if="index < steps.length - 1"
                        class="mt-20 flex justify-center"
                    >
                        <div
                            class="flex flex-col items-center gap-2 text-muted-foreground/30"
                        >
                            <div class="h-8 w-px bg-border/60"></div>
                            <ArrowRight class="h-4 w-4 rotate-90" />
                            <div class="h-8 w-px bg-border/60"></div>
                        </div>
                    </div>
                </section>
            </div>
        </div>

        <!-- Bottom CTA -->
        <section class="border-t border-border/40 py-20">
            <div class="mx-auto max-w-5xl px-4 text-center">
                <h2
                    class="text-2xl font-semibold tracking-tight text-foreground"
                >
                    Prêt à structurer votre revue ?
                </h2>
                <p class="mx-auto mt-3 max-w-md text-muted-foreground">
                    Créez votre projet et importez vos premières références en
                    quelques minutes.
                </p>
                <div
                    class="mt-8 flex flex-wrap items-center justify-center gap-4"
                >
                    <Button
                        as-child
                        size="lg"
                        class="rounded-full bg-brand px-8 font-medium hover:bg-brand/85"
                    >
                        <Link :href="register()">Commencer gratuitement</Link>
                    </Button>
                    <Button
                        as-child
                        variant="outline"
                        size="lg"
                        class="rounded-full px-8 font-medium"
                    >
                        <Link href="/#features">Voir les fonctionnalités</Link>
                    </Button>
                </div>
            </div>
        </section>

        <FooterSection />
    </AppHeaderLayoutLanding>
</template>
